/**
 * Text outreach states, as derived by the consultant importer from
 * mobile_opt_in, text_delivered and contacted. Null on a voter means there is
 * no consultant data for that person, which the UI calls "No text data".
 *
 * The six colours are literal hexes, not tokens: tokens.css carries no
 * pin-state palette of its own, and (the load-bearing reason) var() does not
 * resolve inside an SVG fill or stroke attribute, which is how every map pin
 * is painted. They are distinct from each other and from every hex in the
 * Support, Coverage and household palettes in mapColors.ts, so a bar here and
 * a pin there can never read as the same thing.
 * This is the outreach map layer plan's palette, adopted value for value;
 * mapColors.ts's _textOn reads OUTREACH_DARK_INK_FILLS below to decide dark
 * versus white numerals, so a recolour of any of the six cannot silently
 * desync the two modules.
 *
 * The hexes are literal on purpose. Map pins are SVG strings injected through
 * L.divIcon, and a var() reference inside an SVG fill or stroke presentation
 * attribute does not resolve there, which is why tokens.css cannot be the
 * source for these even though it carries amber, blue and grey values.
 */

export type TextOutreach = 'not_contacted' | 'reached' | 'delivered' | 'opted_in' | 'opted_out';

export const OUTREACH_PRECEDENCE: TextOutreach[] = [
  'opted_in',
  'opted_out',
  'delivered',
  'reached',
  'not_contacted',
];

export const OUTREACH_LABELS: Record<TextOutreach | 'none', string> = {
  opted_in: 'Opted in',
  opted_out: 'Opted out',
  delivered: 'Text delivered, no reply',
  reached: 'Reached out',
  not_contacted: 'Not contacted',
  none: 'No text data',
};

export const OUTREACH_COLORS: Record<TextOutreach | 'none', string> = {
  opted_in: '#1e9e57',      // green
  opted_out: '#d81b60',     // pink red
  delivered: '#e5a50a',     // amber
  reached: '#2f6fd0',       // blue
  not_contacted: '#9aa0a6', // gray
  none: '#d9d9d9',          // light gray
};

/**
 * States light enough that a white numeral fails contrast (an 11px bold
 * count badge needs 4.5:1 outdoors; opted_in's green measures 3.46:1 for
 * white but about 5.0:1 for dark). Named by state, not retyped hex, so this
 * list can never drift from OUTREACH_COLORS above.
 */
const OUTREACH_DARK_INK_STATES: (TextOutreach | 'none')[] = [
  'delivered',
  'not_contacted',
  'none',
  'opted_in',
];

/** The hex fills OUTREACH_DARK_INK_STATES names, for a plain string lookup. */
export const OUTREACH_DARK_INK_FILLS: string[] = OUTREACH_DARK_INK_STATES.map(
  s => OUTREACH_COLORS[s],
);

// Parameter is string, not TextOutreach | 'none', for two callers: orderPivot's
// colorOf option in AdminDashboards is typed (key: string) => string, and the
// state arrives from JSON, so it is only a string at runtime.
export function outreachColor(state: string): string {
  return OUTREACH_COLORS[state as TextOutreach | 'none'] ?? OUTREACH_COLORS.none;
}

/**
 * Null or undefined means there is no consultant data for that voter, which
 * reads as "No text data". An unrecognised state falls through to its own raw
 * key so a new state from a future delivery is readable instead of blank. This
 * is the signature the outreach map layer plan needs; the dashboards only ever
 * pass a known state.
 */
export function outreachLabel(
  state: TextOutreach | 'none' | null | undefined,
): string {
  if (!state) return OUTREACH_LABELS.none;
  return OUTREACH_LABELS[state as TextOutreach | 'none'] ?? state;
}

/**
 * The state a household shows: the first state in OUTREACH_PRECEDENCE that
 * any member has, or 'none' when no member has one.
 *
 * Takes the members' states, not the members, so it stays free of voter and
 * map types. mapColors.householdOutreachStateOf() is the wrapper that pulls
 * the states off a household and drops do-not-contact members.
 */
export function householdOutreachState(
  states: (TextOutreach | null | undefined)[],
): TextOutreach | 'none' {
  for (const candidate of OUTREACH_PRECEDENCE) {
    if (states.some(s => s === candidate)) return candidate;
  }
  return 'none';
}
